import React from 'react';
import {
  debounce,
  filter,
  find,
  forEach,
  get,
  has,
  identity,
  isEmpty,
  isEqual,
  map,
  mapValues,
  trim,
  values,
} from 'lodash';
import classnames from 'classnames';
import { CircularProgress, Typography } from '@mui/material';
import DebouncedText from '../../elements/editors/mui/DebouncedText';
import FrequencyField from '../../elements/FrequencyField';
import { Button } from 'new/elements/support/Button';
import LoadableMonacoEditor from '../../elements/editors/monaco/LoadableMonacoEditor';
import { getEditorLanguage } from '../../elements/editors/monaco/MonacoEditorUtils';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  redesignStyles,
  SelectInput,
  SelectOption,
} from '../../elements/rselect/ReactSelectUtils';
import SortedSelect from '../../elements/rselect/SortedSelect';
import ConfigureAPI from './ConfigureAPI';
import { CONNECTION_CATEGORY_REST } from 'js/constants/connectionCategories';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { formatCode, stringifyObject } from 'js/lib/FormatUtils';
import UIUtils from '../../../lib/UIUtils';
import { SUPPORT_KEY_SOURCE_FORM_REST_TEMP } from 'js/constants/supportPageKeys';
import DataSourceDetails from './DataSourceDetails/DataSourceDetails';
import MonitoringInterval from './MonitoringInterval/MonitoringInterval';
import './ConfigureNexlaMonitor.scss';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';

const CHECK_BOX_LABEL_STYLE = {
  fontSize: 12,
  fontWeight: 500,
  color: '#1F2F40',
  lineHeight: '24px',
  textTransform: 'initial',
  letterSpacing: '.5px',
};

const navOptions = [
  {
    label: 'Template',
    id: 'configure-templatized-api-container',
  },
  {
    label: 'Schedule Ingestion',
    id: 'scheduling',
  },
];

class ConfigureNexlaMonitor extends React.Component {
  constructor(props) {
    super(props);

    this._getSimpleModeContent = this._getSimpleModeContent.bind(this);
    this._getAdvancedModeContent = this._getAdvancedModeContent.bind(this);
    this._getEditorInputField = this._getEditorInputField.bind(this);
    this._getTextField = this._getTextField.bind(this);
    this._getSelectField = this._getSelectField.bind(this);
    this._handleModeChanged = this._handleModeChanged.bind(this);
    this._handleCronChanged = this._handleCronChanged.bind(this);
    this._handleAdvancedSettingChanged = this._handleAdvancedSettingChanged.bind(this);
    this._handleCallAPIClicked = this._handleCallAPIClicked.bind(this);
    this._getPathMatches = this._getPathMatches.bind(this);
    this._getResourceDataFields = this._getResourceDataFields.bind(this);
    this._getResourceDataFieldKey = this._getResourceDataFieldKey.bind(this);
    this._loadResourceDataFieldOptions = this._loadResourceDataFieldOptions.bind(this);
  }

  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_REST_TEMP);
  }

  componentDidMount() {
    const {
      paging: { sourceConfigById },
      selectedConnection,
      selectedCredential,
      loadDataMaps,
    } = this.props;
    const sourceConfigFields = sourceConfigById[selectedConnection];

    if (find(sourceConfigFields, { type: 'data_map' })) {
      loadDataMaps(false, ACLQueryModeTypes.MODE_COLLABORATOR.key);
    }

    if (selectedCredential && !selectedCredential.credentials_non_secure_data) {
      this.props.getDataCredential(selectedCredential.id);
    }

    this._loadResourceDataFieldOptions();

    this.props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_REST_TEMP);
  }

  componentDidUpdate(prevProps) {
    const {
      paging: { metaInfoById, sourceConfigById },
      vendorEndpoints,
      selectedConnection,
      apiResponseByStage,
      validateDataPath,
    } = this.props;

    const { connectionTypeCategory } = metaInfoById[selectedConnection];
    const endpointId = undefined;
    // const endpointId = templatizedAPIConfig.vendor_endpoint_id;

    if (connectionTypeCategory === CONNECTION_CATEGORY_REST) {
      // if call response changed, validate data path
      if (apiResponseByStage && apiResponseByStage !== prevProps.apiResponseByStage) {
        const endpointIds = { 0: endpointId };

        forEach(this._getResourceDataFields(), (field) => {
          endpointIds[this._getResourceDataFieldKey(field)] = field.vendor_endpoint_id;
        });

        forEach(endpointIds, (endpointId, key) => {
          if (
            apiResponseByStage[key] &&
            apiResponseByStage[key].apiResponseCode &&
            !isEqual(apiResponseByStage[key], prevProps.apiResponseByStage[key])
          ) {
            const endpoint = vendorEndpoints[endpointId];
            const iterations = get(endpoint, 'source_template["rest.iterations"]', []);
            const stageAPIConfig = iterations.length > 0 ? iterations[iterations.length - 1] : {};

            validateDataPath(stageAPIConfig, key);
          }
        });
      }

      // if the newly selected endpoint has some vendor_resource_data params,
      // get data for them
      if (
        (endpointId && endpointId !== prevProps.templatizedAPIConfig.vendor_endpoint_id) ||
        sourceConfigById !== prevProps.paging.sourceConfigById
      ) {
        this._loadResourceDataFieldOptions();
      }
    }
  }

  render() {
    const {
      paging: { sourceConfigById },
      nexlaMonitorApiConfig,
      sourceName,
      sourceDescription,
      onInputChange,
      selectedConnectionDetails,
      visibleFieldData,
    } = this.props;

    const isAdvancedMode = nexlaMonitorApiConfig && nexlaMonitorApiConfig.advanced_templatized_api;

    const cronField = sourceConfigById?.rest?.find((field) => field.name === 'start.cron');

    return (
      <div className="configure-nexla-monitor">
        <DataSourceDetails
          name={sourceName}
          description={sourceDescription}
          onInputChange={onInputChange}
          selectedConnectionDetails={selectedConnectionDetails}
        />
        <div className="nav-scroller-container">
          <div
            id="configure-templatized-api-container"
            className="configure-templatized-api-container advanced-configuration-left-content"
          >
            <div className="form-section-tabs-container">
              <div
                data-mode="simple"
                className={classnames({
                  'form-section-tab': true,
                  selected: !isAdvancedMode,
                })}
                onClick={this._handleModeChanged}
                data-testid="simple-tab"
              >
                <h1>Template</h1>
              </div>
              <div
                data-mode="advanced"
                className={classnames({
                  'form-section-tab': true,
                  selected: isAdvancedMode,
                })}
                onClick={this._handleModeChanged}
                data-testid="advanced-tab"
              >
                <h1>Advanced</h1>
              </div>
            </div>

            <div
              className={classnames({
                'form-section-tab-content': true,
              })}
            >
              {isAdvancedMode
                ? this._getAdvancedModeContent(cronField)
                : this._getSimpleModeContent(cronField)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  _getSimpleModeContent(cronField) {
    const {
      paging: { metaInfoById },
      probeGetSample,
      nmTemplates,
      selectedConnection,
      apiResponse,
      dataMaps,
      isLoadingProbeSample,
      nexlaMonitorApiConfig,
      dataPathValidationResult,
      dataPathValidationResultByStage,
    } = this.props;

    // Checking for template data
    if (!nmTemplates[selectedConnection]) return null;

    let selectedTemplate = nexlaMonitorApiConfig['template'] || {};

    let visibleFields = !isEmpty(selectedTemplate) ? selectedTemplate.fields : [];

    visibleFields = filter(visibleFields, (field) => {
      return (
        UIUtils.shouldHideField(field, nexlaMonitorApiConfig) === false &&
        UIUtils.shouldRenderField(field, nexlaMonitorApiConfig)
      );
    });

    const pathValidationFailed =
      dataPathValidationResult &&
      !dataPathValidationResult.status &&
      !get(dataPathValidationResult, '["response.data.path"].status', true);
    const pathMatches = this._getPathMatches();
    const pathValidationMessage = get(dataPathValidationResult, '["response.data.path"].message');

    let apiResponseContent;
    let apiResponseContentType;
    let apiResponseCode;
    let apiResponseMessage;

    if (apiResponse) {
      apiResponseContent = apiResponse.apiResponseContent;
      apiResponseContentType = apiResponse.apiResponseContentType;
      apiResponseCode = apiResponse.apiResponseCode;
      apiResponseMessage = apiResponse.apiResponseMessage;
    } else {
      apiResponseContent = '';
      apiResponseContentType = 'text/plain';
      apiResponseCode = null;
      apiResponseMessage = '';
    }

    const editorContent =
      !pathValidationFailed && pathMatches !== null
        ? pathMatches
        : formatCode(apiResponseContent, apiResponseContentType);

    const responseFailure = apiResponseCode >= 400;

    const connectionNameLabel =
      selectedConnection && has(metaInfoById, selectedConnection)
        ? metaInfoById[selectedConnection].displayName
        : 'API';
    const editorLanguage = getEditorLanguage(apiResponseContentType);

    let templates = nmTemplates[selectedConnection] || [];
    let templateOptions = map(templates, (template) => {
      return {
        ...template,
        value: template.id || template.name,
      };
    });

    const isAdvancedMode = get(nexlaMonitorApiConfig, 'advanced_templatized_api', false);

    return (
      <>
        <div className="helper-section">
          <Typography variant="subtitle2" className="helper-section-title">
            Templated API Source
          </Typography>
          <Typography variant="subtitle2" className="helper-section-description">
            Use the Templated API Source to fetch data from any valid API endpoint.
          </Typography>
        </div>
        <NavScroller navOptions={navOptions} navClassName="left-nav">
          <NavScrollerContext.Consumer>
            {(consumer) => {
              return (
                <div className="template-content">
                  <div className="configure-templatized-api configure-template">
                    <section
                      className={classnames('configuration-holder', {
                        'form-section-tab-content-focused':
                          consumer?.activeScroll === 'configure-templatized-api-container',
                      })}
                    >
                      <div className="body-section">
                        <div className="description">
                          <p>
                            Select and configure the API endpoint you wish to fetch data from. Don't
                            see an option for the endpoint you wish to use? No problem! You can use
                            the REST API source to fetch data from any valid{' '}
                            {`${connectionNameLabel}`} URL.
                          </p>
                        </div>
                        <ol>
                          <li>
                            <div className="input-row">
                              <div className="input-holder">
                                <label
                                  htmlFor="template "
                                  className="
                            endpoint-label
                          "
                                >
                                  Template
                                </label>
                                {this._getTemplateDropDownField('template', templateOptions)}
                              </div>
                              <div className="call-api-button-container call-soap-button-container">
                                <Button
                                  variant="contained"
                                  color="primary"
                                  disabled={isEmpty(selectedTemplate)}
                                  onClick={probeGetSample}
                                  className={classnames('call-api-button', {
                                    loading: isLoadingProbeSample,
                                  })}
                                  data-testid="call-api-button"
                                >
                                  {isLoadingProbeSample ? (
                                    <CircularProgress
                                      size={19}
                                      sx={{
                                        color: 'var(--color-danger-700)',
                                      }}
                                    />
                                  ) : (
                                    <>Test</>
                                  )}
                                </Button>
                              </div>
                            </div>
                            {!isEmpty(selectedTemplate) &&
                              (selectedTemplate.description ||
                                (has(selectedTemplate, 'info_url') &&
                                  selectedTemplate.info_url)) && (
                                <div className="data-path-suggestions">
                                  <h4>Details</h4>
                                  <div className="suggestions-container endpoint-info-container">
                                    {selectedTemplate.description && (
                                      <div>
                                        {UIUtils.stringToParagraphs(selectedTemplate.description)}
                                      </div>
                                    )}
                                    {has(selectedTemplate, 'info_url') &&
                                      selectedTemplate.info_url && (
                                        <a
                                          className="endpoint-doc-link undecorated-link"
                                          href={selectedTemplate.info_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          Open Documentation
                                        </a>
                                      )}
                                  </div>
                                </div>
                              )}
                          </li>
                          {!isEmpty(visibleFields) &&
                            map(visibleFields, (field) => {
                              let fieldComponent;

                              if (!isEmpty(field.options)) {
                                fieldComponent = (
                                  <div className="input-holder">
                                    <label htmlFor={field.name}>{field.display_name}</label>
                                    {this._getSelectField(field.name, field.options)}
                                  </div>
                                );
                              } else if (field.type === 'frequency') {
                                fieldComponent = (
                                  <div className="input-holder">
                                    <FrequencyField
                                      name={field.name}
                                      label="Fetch Data"
                                      value={templatizedAPIConfig[field.name]}
                                      labelStyle={CHECK_BOX_LABEL_STYLE}
                                      iconColor="#4AD496"
                                      onChange={this._handleCronChanged}
                                    />
                                  </div>
                                );
                              } else if (field.type === 'data_map') {
                                fieldComponent = (
                                  <div className="input-holder">
                                    <label htmlFor={field.name} className="endpoint-label">
                                      {field.display_name}
                                    </label>
                                    {this._getSelectField(field.name, dataMapOptions)}
                                  </div>
                                );
                              } else if (field.type === 'data_map_column') {
                                fieldComponent = (
                                  <div className="input-holder">
                                    <label htmlFor={field.name} className="endpoint-label">
                                      {field.display_name}
                                    </label>
                                    {this._getSelectField(
                                      field.name,
                                      dataMapColumnOptions[field.name] || [],
                                    )}
                                  </div>
                                );
                              } else if (field.type === 'vendor_resource_data') {
                                const fieldKey = this._getResourceDataFieldKey(field);
                                const data = get(
                                  dataPathValidationResultByStage,
                                  `${fieldKey}['response.data.path'].matches`,
                                );
                                const options = map(
                                  filter(data, (item) =>
                                    has(item, field.option_property_mapping.value),
                                  ),
                                  (item) =>
                                    mapValues(field.option_property_mapping, (propertyPath) =>
                                      get(
                                        item,
                                        propertyPath,
                                        get(item, field.option_property_mapping.value),
                                      ),
                                    ),
                                );

                                fieldComponent = (
                                  <div className="input-holder">
                                    <label htmlFor={field.name} className="endpoint-label">
                                      {field.display_name}
                                    </label>
                                    {this._getSelectField(field.name, options, {
                                      isCreatable: true,
                                      formatCreateLabel: identity,
                                      isDisabled: isLoadingProbeSample,
                                      placeholder: 'Select or type...',
                                    })}
                                  </div>
                                );
                              } else if (field.type === 'sql_editor') {
                                fieldComponent = (
                                  <div className="input-holder">
                                    <label htmlFor={field.name} className="endpoint-label">
                                      {field.display_name}
                                    </label>
                                    {this._getEditorInputField(field.name, {})}
                                  </div>
                                );
                              } else {
                                fieldComponent = (
                                  <div className="input-holder">
                                    <label
                                      htmlFor={field.name}
                                      className="
                            endpoint-label
                          "
                                    >
                                      {field.display_name}
                                    </label>
                                    {this._getTextField(
                                      field.name,
                                      field.inputType === 'textarea'
                                        ? {
                                            multiline: true,
                                            minRows: 4,
                                            maxRows: 8,
                                          }
                                        : {},
                                    )}
                                  </div>
                                );
                              }

                              return (
                                <div key={field.name} className="input-row">
                                  {fieldComponent}
                                </div>
                              );
                            })}
                        </ol>
                      </div>
                    </section>
                    <section className="api-response-holder">
                      <header className="response-header">
                        <span className="response-title" data-testid="response-title">
                          {responseFailure ? 'Failure:' : 'Endpoint Test Result'}
                        </span>

                        {responseFailure && (
                          <span className="response-failure">{apiResponseMessage}</span>
                        )}

                        {!responseFailure && dataPathValidationResult && (
                          <span
                            className={classnames(
                              'data-path-validation-result',
                              {
                                'invalid-data-path': pathValidationFailed,
                              },
                              'response-success',
                            )}
                          >
                            {pathValidationMessage}
                          </span>
                        )}
                      </header>
                      <LoadableMonacoEditor
                        theme="dark"
                        language={editorLanguage}
                        value={editorContent}
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                        }}
                      />
                    </section>
                  </div>
                  {isAdvancedMode ? null : (
                    <div id="scheduling">
                      <MonitoringInterval
                        field={cronField}
                        source={this.props.sourceBeingEdited}
                        config={this.props.visibleFieldData}
                        monitorIntervalContainerClassname={classnames({
                          'frequency-style': true,
                          'frequency-focused': consumer?.activeScroll === 'scheduling',
                        })}
                        onConfigChanged={this._handleAdvancedSettingChanged}
                      />
                    </div>
                  )}
                </div>
              );
            }}
          </NavScrollerContext.Consumer>
        </NavScroller>
      </>
    );
  }

  _getAdvancedModeContent(cronField) {
    const {
      paging,
      dataMaps,
      dataMapSamples,
      selectedConnection,
      apiConfig,
      apiResponseByStage,
      apiResponseUpdateTime,
      isLoadingProbeSample,
      dataPathValidationResultByStage,
      validateDataPath,
      loadDataMaps,
      setFormError,
      onClearAPIResponse,
      onCallRESTAPI,
      onAPIConfigChanged,
      onGetParameterizedURLs,
      onAddStep,
      onRemoveStep,
    } = this.props;

    return (
      <ConfigureAPI
        showAdvancedSettings={false}
        advancedFields={[cronField]}
        onSourceConfigChanged={this.props.onSourceConfigChanged}
        paging={paging}
        dataMaps={dataMaps}
        dataMapSamples={dataMapSamples}
        selectedConnection={selectedConnection}
        apiConfig={apiConfig}
        apiResponseByStage={apiResponseByStage}
        apiResponseUpdateTime={apiResponseUpdateTime}
        isLoadingProbeSample={isLoadingProbeSample}
        dataPathValidationResultByStage={dataPathValidationResultByStage}
        validateDataPath={validateDataPath}
        loadDataMaps={loadDataMaps}
        setFormError={setFormError}
        onClearAPIResponse={onClearAPIResponse}
        onCallAPI={onCallRESTAPI}
        onAPIConfigChanged={onAPIConfigChanged}
        onGetParameterizedURLs={onGetParameterizedURLs}
        onAddStep={onAddStep}
        sourceBeingEdited={this.props.sourceBeingEdited}
        onRemoveStep={onRemoveStep}
        setContextualSupport={this.props.setContextualSupport}
        unsetContextualSupport={this.props.unsetContextualSupport}
        flowType={this.props.flowType}
      />
    );
  }

  _getEditorInputField(name, props = {}) {
    const { nexlaMonitorApiConfig, onNexlaMonitorConfigChanged } = this.props;

    return (
      <LoadableMonacoEditor
        language="sql"
        value={get(nexlaMonitorApiConfig, name, '')}
        onChange={debounce((text) => onNexlaMonitorConfigChanged(name, text), 200)}
        theme="dark"
        height={250}
        options={{
          minimap: { enabled: false },
        }}
      />
    );
  }

  _getTextField(name, props = {}) {
    const { nexlaMonitorApiConfig, onNexlaMonitorConfigChanged } = this.props;

    return (
      <DebouncedText
        id={name}
        value={get(nexlaMonitorApiConfig, name, '')}
        onChange={(text) => onNexlaMonitorConfigChanged(name, trim(text))}
        {...props}
      />
    );
  }

  _getSelectField(name, options, customProps = {}) {
    const { nexlaMonitorApiConfig, onNexlaMonitorConfigChanged } = this.props;
    const currentValue = getCurrentSelectedOption(
      options,
      nexlaMonitorApiConfig[name],
      null,
      null,
      !!customProps.isCreatable,
    );
    return (
      <SortedSelect
        id={name}
        value={currentValue}
        options={options}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        backspaceRemovesValue={false}
        isSearchable={true}
        onChange={(currValue) => {
          const newValue = getNewSelectionValue(currValue, true);
          onNexlaMonitorConfigChanged(name, newValue);
        }}
        className="dropdown"
        styles={{
          ...redesignStyles,
          control: (base) => base,
        }}
        {...customProps}
      />
    );
  }

  _getTemplateDropDownField(name, options, customProps = {}) {
    const { nexlaMonitorApiConfig, onNexlaMonitorConfigChanged, selectedConnection, nmTemplates } =
      this.props;
    let templates = nmTemplates[selectedConnection] || [];
    let template = nexlaMonitorApiConfig[name] || {};
    return (
      <SortedSelect
        id={name}
        value={template}
        options={options}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        backspaceRemovesValue={false}
        isSearchable={true}
        onChange={(currValue) => {
          onNexlaMonitorConfigChanged(name, currValue);
        }}
        className="dropdown"
        styles={{
          ...redesignStyles,
          control: (base) => base,
        }}
        {...customProps}
      />
    );
  }

  _handleModeChanged(event) {
    const {
      nexlaMonitorApiConfig,
      onClearAPIResponse,
      onNexlaMonitorConfigChanged,
      onAPIConfigChanged,
    } = this.props;
    const { mode } = event.currentTarget.dataset;
    const isAdvancedMode = get(nexlaMonitorApiConfig, 'advanced_templatized_api', false);
    const isNewAdvancedMode = mode === 'advanced';

    if ((isAdvancedMode && !isNewAdvancedMode) || (!isAdvancedMode && isNewAdvancedMode)) {
      onClearAPIResponse();
    }
    onNexlaMonitorConfigChanged('advanced_templatized_api', isNewAdvancedMode);
  }

  _handleCronChanged(value, fieldName) {
    this.props.onNexlaMonitorConfigChanged(fieldName, value);
  }

  _handleAdvancedSettingChanged(data, name, value) {
    this.props.onNexlaMonitorConfigChanged(name, value, data);
    this.props.onAPIConfigChanged(name, value);
  }

  _handleCallAPIClicked() {
    this.props.onCallAPI();
  }

  _getPathMatches() {
    const { dataPathValidationResult, apiResponse } = this.props;
    const matches = get(dataPathValidationResult, '["response.data.path"].matches');

    if (!matches) {
      return null;
    }

    const contentType = get(apiResponse, 'apiResponseContentType');
    const matchesString = stringifyObject(matches, contentType);

    return formatCode(matchesString, contentType);
  }

  _getResourceDataFields() {
    const {
      paging: { sourceConfigById },
      selectedConnection,
      nexlaMonitorApiConfig,
    } = this.props;
    const sourceConfigFields = sourceConfigById[selectedConnection];

    return filter(
      sourceConfigFields,
      (field) =>
        field.type === 'vendor_resource_data' &&
        UIUtils.shouldRenderField(field, nexlaMonitorApiConfig),
    );
  }

  _getResourceDataFieldKey(field) {
    return `vendor_resource_data_${field.param_id}`;
  }

  _loadResourceDataFieldOptions() {
    const {
      paging: { metaInfoById },
      selectedConnection,
      selectedCredentialId,
      templatizedAPIConfig,
      apiResponseByStage,
      probeGetSample,
    } = this.props;
    const { connectionTypeCategory } = metaInfoById[selectedConnection];
    // const endpointId = templatizedAPIConfig.vendor_endpoint_id;

    // if (endpointId && connectionTypeCategory === CONNECTION_CATEGORY_REST) {
    //   forEach(this._getResourceDataFields(), (field) => {
    //     const fieldKey = this._getResourceDataFieldKey(field);

    //     if (!apiResponseByStage[fieldKey]) {
    //       probeGetSample(selectedCredentialId, fieldKey, {
    //         vendor_endpoint_id: field.vendor_endpoint_id,
    //         template_config: field.template_config,
    //         resource_type: 'SOURCE'
    //       });
    //     }
    //   });
    // }
  }
}

export default ConfigureNexlaMonitor;
